import React, { useState, useEffect } from 'react';
import { 
  Truck, User, Home, Users, FileText, 
  ChevronDown, ChevronRight, Package, List, Clock,
  Activity, Settings, X, Menu, LogOut, Map, MapPin, Car
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import Clients from '@/pages/admin/components/Clients';
import AllMap from '@/pages/admin/components/location/AllMap';

const Sidebar = ({ 
  sidebarCollapsed, 
  setSidebarCollapsed,
  activeSection, 
  setActiveSection,
  user
}) => {
  const [expandedSections, setExpandedSections] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const userData = JSON.parse(localStorage.getItem("user") || '{}');
  
  // Funcție pentru calcularea salutului în funcție de ora zilei
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      return 'Bună dimineața';
    } else if (hour >= 12 && hour < 18) {
      return 'Bună ziua';
    } else {
      return 'Bună seara';
    }
  };

  // Detectează dacă este mobil
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Închide meniul mobil când se schimbă secțiunea
  useEffect(() => {
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  }, [activeSection, isMobile]);

  // Închide meniul mobil când se apasă ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [mobileMenuOpen]);

  const toggleSection = (sectionId) => {
    if (sidebarCollapsed && !isMobile) return;
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleItemClick = (item) => {
    if (item.hasSubItems) {
      toggleSection(item.id);
      if (!expandedSections[item.id] && item.subItems?.length > 0) {
        setActiveSection(item.subItems[0].id);
      }
    } else {
      setActiveSection(item.id);
    }
    
    // Închide meniul mobil după selecție
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const sidebarItems = [
    { 
      id: 'dashboard', 
      label: 'Dashboard Principal', 
      icon: Home,
      description: 'Monitorizare generală'
    },
    { 
      id: 'expedieri', 
      label: 'Expedieri', 
      icon: Truck,
      description: 'Gestionare transport',
      hasSubItems: true,
      subItems: [
        { 
          id: 'expedieri', 
          label: 'Toate Expedierile', 
          icon: List,
          description: 'Listă completă'
        },
        { 
          id: 'expedieri-finalizate', 
          label: 'Expedieri Finalizate', 
          icon: Clock,
          description: 'Istoric complet'
        },
      ]
    },

    {
      id: 'clienti',
      label: 'Clienți',
      icon: Users,
      description: 'Gestionare clienți',
      component: <Clients />
    },
    { 
      id: 'rapoarte', 
      label: 'Rapoarte Operaționale', 
      icon: FileText,
      description: 'Analiză și statistici'
    }
  ];

  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/auth/logout`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });

      logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error("Eroare la logout:", error);
    }
  };

  const UserInfo = ({ collapsed = false, mobile = false }) => (
    <div className={`${mobile ? 'p-4' : 'p-3'} border-t border-slate-700`}>
      <div className={`flex items-center ${!collapsed ? 'mb-3' : ''}`}>
        {/* Avatar cu gradient */}
        <div className="relative">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg ring-2 ring-emerald-500/20">
            <User className="w-5 h-5 text-white" />
          </div>
          <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-slate-800"></div>
        </div>
        
        {!collapsed && (
          <div className="ml-3 flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">
              {userData?.name || 'Utilizator'}
            </p>
            <p className="text-xs text-emerald-300 truncate">
              {getGreeting()}
            </p>
          </div>
        )}
      </div>

      {/* Butoane de acțiuni */}
      {!collapsed && (
        <div className="flex items-center gap-2">
          <button 
            onClick={handleLogout}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-all duration-200 group"
          >
            <LogOut className="w-4 h-4 text-red-400 group-hover:text-red-300" />
            <span className="text-xs text-slate-400 group-hover:text-slate-300">LogOut</span>
          </button>
        </div>
      )}
    </div>
  );

  // Render pentru desktop - Design îmbunătățit
  return (
    <div className={`fixed left-0 top-0 bg-slate-800 shadow-lg transition-all duration-300 ${sidebarCollapsed ? 'w-16' : 'w-64'} flex flex-col h-screen z-40 hidden md:flex`}>
      
      {/* Logo/Brand - Design îmbunătățit */}
      <div className="p-4 border-b border-slate-700/50 bg-slate-900/50">
        <div className="flex items-center">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg ring-2 ring-blue-500/30">
            <img 
              src="logo.png" 
              alt="Logo" 
              className="w-7 h-7 object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <Truck className="w-7 h-7 text-white hidden" />
          </div>
          {!sidebarCollapsed && (
            <div className="ml-3">
              <h1 className="text-lg font-bold text-white">
                Trans Dispecerat
              </h1>
              <p className="text-xs text-blue-300">
                Sistem Management
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation - Design îmbunătățit */}
      <nav className="flex-1 p-4 overflow-y-auto">
        <div className="space-y-2">
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            const isExpanded = expandedSections[item.id];
            const hasActiveSubItem = item.subItems?.some(subItem => subItem.id === activeSection);
            const isMainItemActive = activeSection === item.id;
            
            return (
              <div key={item.id}>
                {/* Main Item */}
                <button
                  onClick={() => handleItemClick(item)}
                  className={`group w-full flex items-center justify-between px-4 py-3.5 rounded-xl text-left transition-all duration-200 ${
                    (isMainItemActive || hasActiveSubItem)
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg ring-1 ring-blue-500/30'
                      : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                  }`}
                >
                  <div className="flex items-center min-w-0">
                    <Icon className={`w-5 h-5 flex-shrink-0 ${
                      (isMainItemActive || hasActiveSubItem) 
                        ? 'text-white' 
                        : 'text-slate-400 group-hover:text-white'
                    }`} />
                    
                    {!sidebarCollapsed && (
                      <div className="ml-3 min-w-0">
                        <div className="text-sm font-semibold truncate">
                          {item.label}
                        </div>
                        <div className={`text-xs mt-0.5 truncate ${
                          (isMainItemActive || hasActiveSubItem)
                            ? 'text-blue-100'
                            : 'text-slate-500 group-hover:text-slate-400'
                        }`}>
                          {item.description}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {!sidebarCollapsed && item.hasSubItems && (
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 flex-shrink-0 ${
                      isExpanded ? 'rotate-180' : ''
                    }`} />
                  )}
                </button>

                {/* Sub Items */}
                {!sidebarCollapsed && item.hasSubItems && isExpanded && item.subItems && (
                  <div className="ml-3 mt-2 space-y-1">
                    {item.subItems.map((subItem) => {
                      const SubIcon = subItem.icon;
                      return (
                        <button
                          key={subItem.id}
                          onClick={() => setActiveSection(subItem.id)}
                          className={`group w-full flex items-center px-4 py-3 rounded-lg text-left transition-all duration-200 ${
                            activeSection === subItem.id
                              ? 'bg-slate-700/70 text-blue-300 border-l-3 border-blue-400'
                              : 'text-slate-400 hover:bg-slate-700/30 hover:text-slate-200'
                          }`}
                        >
                          <SubIcon className={`w-4 h-4 flex-shrink-0 ${
                            activeSection === subItem.id 
                              ? 'text-blue-300' 
                              : 'text-slate-500 group-hover:text-slate-300'
                          }`} />
                          <div className="ml-3 min-w-0">
                            <div className="text-sm font-medium truncate">
                              {subItem.label}
                            </div>
                            <div className={`text-xs mt-0.5 truncate ${
                              activeSection === subItem.id
                                ? 'text-blue-200'
                                : 'text-slate-500 group-hover:text-slate-400'
                            }`}>
                              {subItem.description}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </nav>

      {/* Desktop User Info */}
      <UserInfo collapsed={sidebarCollapsed} />
    </div>
    
  );
};

export default Sidebar;